import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import { useAuth } from '@/hooks';
import { LoginFormData } from '@/types';
import TwoFactorDialog from '@/components/auth/TwoFactorDialog';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import {
  googleLogin,
  selectTwoFactorToken,
  selectTwoFactorSetupRequired,
  selectTwoFactorQrCode,
  selectTwoFactorSecret,
  clear2FA,
} from '@/store/slices/authSlice';

// Google Identity Services types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: string | number;
              locale?: string;
            }
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const GOOGLE_CLIENT_ID = '141913349498-ndgdog59ug4g98kf81r0j138nek6d2fc.apps.googleusercontent.com';

// =====================================================
// SCHEMA DE VALIDAÇÃO
// =====================================================

const schema = yup.object({
  email: yup
    .string()
    .email('Email inválido')
    .required('Email é obrigatório'),
  password: yup
    .string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .required('Senha é obrigatória'),
  rememberMe: yup.boolean(),
});

// =====================================================
// COMPONENTE
// =====================================================

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    twoFactorRequired,
    canAttemptLogin,
    getTimeUntilNextAttempt,
    login,
    clearError,
  } = useAuth();

  // Selectors para 2FA
  const twoFactorToken = useAppSelector(selectTwoFactorToken);
  const twoFactorSetupRequired = useAppSelector(selectTwoFactorSetupRequired);
  const twoFactorQrCode = useAppSelector(selectTwoFactorQrCode);
  const twoFactorSecret = useAppSelector(selectTwoFactorSecret);

  const [showPassword, setShowPassword] = useState(false);
  const [lockoutTimeRemaining, setLockoutTimeRemaining] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleButtonRef = React.useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  // Redirect se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location]);

  // Limpar erro ao montar componente
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Atualizar contador de lockout
  useEffect(() => {
    const updateLockoutTimer = () => {
      const timeRemaining = getTimeUntilNextAttempt();
      setLockoutTimeRemaining(timeRemaining);
    };

    updateLockoutTimer();
    const interval = setInterval(updateLockoutTimer, 1000);

    return () => clearInterval(interval);
  }, [getTimeUntilNextAttempt]);

  // Google Sign-In callback
  const handleGoogleCredentialResponse = useCallback(async (response: { credential: string }) => {
    setGoogleLoading(true);
    try {
      const result = await dispatch(googleLogin(response.credential));

      if (googleLogin.fulfilled.match(result)) {
        // Se 2FA é necessário, não navegar - o dialog será aberto automaticamente
        if (result.payload.twoFactorRequired) {
          // O TwoFactorDialog será aberto via estado do Redux
          return;
        }
        toast.success('Login realizado com sucesso!');
        const from = (location.state as any)?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        toast.error((result.payload as string) || 'Erro no login com Google');
      }
    } catch (err: any) {
      console.error('Erro no login com Google:', err);
      toast.error('Erro inesperado no login com Google');
    } finally {
      setGoogleLoading(false);
    }
  }, [dispatch, navigate, location.state]);

  // Inicializar Google Sign-In
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: '100%',
          locale: 'pt-BR',
        });
      }
    };

    // Aguardar o script do Google carregar
    if (window.google) {
      initializeGoogleSignIn();
    } else {
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);

      // Limpar o intervalo após 10 segundos
      setTimeout(() => clearInterval(checkGoogle), 10000);
    }
  }, [handleGoogleCredentialResponse]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();

      if (!canAttemptLogin()) {
        const minutes = Math.ceil(lockoutTimeRemaining / 60000);
        toast.error(`Muitas tentativas de login. Tente novamente em ${minutes} minutos.`);
        return;
      }

      const result = await login(data);

      if (result.type === 'auth/login/fulfilled') {
        if (!twoFactorRequired) {
          toast.success('Login realizado com sucesso!');
          const from = (location.state as any)?.from?.pathname || '/dashboard';
          navigate(from, { replace: true });
        }
        // Se 2FA é necessário, o dialog será aberto automaticamente
      } else {
        // Erro será mostrado pelo useEffect do error
      }
    } catch (err: any) {
      console.error('Erro no login:', err);
      toast.error('Erro inesperado no login');
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Mostrar erro
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // =====================================================
  // FORMATAÇÃO DO TEMPO
  // =====================================================

  const formatLockoutTime = (milliseconds: number) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          mx: 'auto',
          p: 2,
        }}
      >
        <CardContent>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                Bem-vindo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Faça login em sua conta FalaVipPay
              </Typography>
            </Box>

            {/* Mensagem de lockout */}
            {lockoutTimeRemaining > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Muitas tentativas de login. Tente novamente em{' '}
                {formatLockoutTime(lockoutTimeRemaining)}.
              </Alert>
            )}

            {/* Formulário */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Email */}
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isSubmitting || lockoutTimeRemaining > 0}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* Senha */}
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    margin="normal"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={isSubmitting || lockoutTimeRemaining > 0}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                            disabled={isSubmitting || lockoutTimeRemaining > 0}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* Lembrar-me */}
              <Controller
                name="rememberMe"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        disabled={isSubmitting || lockoutTimeRemaining > 0}
                      />
                    }
                    label="Lembrar-me"
                    sx={{ mt: 1, mb: 2 }}
                  />
                )}
              />

              {/* Botão de login */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || lockoutTimeRemaining > 0 || googleLoading}
                sx={{ mt: 2, mb: 2 }}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <LoginIcon />
                  )
                }
              >
                {isSubmitting ? 'Entrando...' : 'Entrar'}
              </Button>

              {/* Divider */}
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  ou
                </Typography>
              </Divider>

              {/* Google Sign-In Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {googleLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Conectando com Google...
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    ref={googleButtonRef}
                    sx={{
                      width: '100%',
                      '& > div': { width: '100% !important' },
                      '& iframe': { width: '100% !important' },
                    }}
                  />
                )}
              </Box>

              {/* Divider */}
              <Divider sx={{ my: 2 }} />

              {/* Links */}
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  to="/forgot-password"
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    fontSize: '0.875rem',
                  }}
                >
                  <Typography variant="body2" color="primary">
                    Esqueci minha senha
                  </Typography>
                </Link>

                <Typography variant="body2" sx={{ mt: 2 }}>
                  Não tem uma conta?{' '}
                  <Link
                    to="/register"
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    >
                      Criar conta
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

      {/* Dialog de 2FA */}
      <TwoFactorDialog
        open={twoFactorRequired}
        onClose={() => {
          dispatch(clear2FA());
        }}
        tempToken={twoFactorToken || ''}
        isSetup={twoFactorSetupRequired}
        qrCode={twoFactorQrCode || undefined}
        secret={twoFactorSecret || undefined}
      />
    </>
  );
};

export default LoginPage;